import { createFileRoute } from "@tanstack/react-router";
import { pageHead } from "@/lib/seo";
import { Topbar } from "@/components/calv/Topbar";
import { Footer } from "@/components/calv/Footer";
import { GlossaryTerms } from "@/components/calv/GlossaryTerms";
import { LogoReferences } from "@/components/calv/LogoReferences";
import { Hero } from "@/components/calv/Hero";
import { Section } from "@/components/calv/Section";
import { ProcessFlow } from "@/components/calv/ProcessFlow";
import { Capabilities } from "@/components/calv/Capabilities";
import { StackGrid } from "@/components/calv/StackGrid";
import { Callout } from "@/components/calv/Callout";
import { CTABlock } from "@/components/calv/CTABlock";
import { RevealOnScroll } from "@/components/calv/RevealOnScroll";
import { RelatedServices } from "@/components/calv/RelatedServices";
import { relatedBySource } from "@/lib/related-services-data";

export const Route = createFileRoute("/leistungen/conversion-web-optimierung")({
  head: () => {
    const h = pageHead("conversionWebOptimization", "de");
    return { meta: h.meta, links: h.links };
  },
  component: CWOPage,
});

const steps = [
  { num: "01", title: "Analyse von Nutzerverhalten, Funnels und Abbruchstellen", body: "Wir analysieren, wie sich Nutzer tatsächlich auf der Website oder im Online-Shop bewegen – nicht, wie es theoretisch geplant war. Einstiegsseiten, Nutzerpfade, Abbruchstellen, Performance nach Device. Ziel ist exakt zu verstehen, wo Nutzer aussteigen – und warum." },
  { num: "02", title: "Wirtschaftliche Bewertung der identifizierten Schwächen", body: "Nicht jede Schwäche ist automatisch relevant – und nicht jede Optimierung wirtschaftlich sinnvoll. Wir bewerten nach Anzahl betroffener Nutzer, Einfluss auf Abschlüsse und Umsetzungsaufwand. So entsteht eine klare Priorisierung nach wirtschaftlichem Hebel – nicht nach Bauchgefühl." },
  { num: "03", title: "Optimierung von Struktur, Inhalt und Nutzerführung", body: "Auf Basis der Priorisierung optimieren wir die Bereiche mit dem größten Hebel: Seiten- und Inhaltsstruktur, Argumentationslogik, Call-to-Actions, Formulare und Checkouts, mobile Nutzung, technische Performance. Wir denken konsequent aus Sicht der Nutzerentscheidung." },
  { num: "04", title: "Verzahnung mit Performance Marketing & SEO", body: "Conversion-Optimierung wirkt nicht isoliert, sondern kanalübergreifend. Bessere Conversion senkt Kosten pro Klick oder Lead. Höherer ROAS ermöglicht Skalierung. Organischer Traffic wird wirtschaftlich nutzbar. Oft der entscheidende Hebel für profitable Marketing-Maßnahmen." },
];

const capabilities = [
  { name: "Funnel-Analyse", detail: "Einstiegsseiten, Abbruchstellen" },
  { name: "Wirtschaftliche Priorisierung", detail: "nach Hebel, nicht Bauchgefühl" },
  { name: "Seitenstruktur & Inhalt", detail: "Argumentationslogik, Reihenfolge" },
  { name: "CTA-Optimierung", detail: "Platzierung, Formulierung, Kontext" },
  { name: "Formulare & Checkouts", detail: "Lead-Strecken, Abschlussraten" },
  { name: "Mobile Nutzung", detail: "Interaktion, Performance" },
  { name: "Technische Performance", detail: "Ladezeiten, Core Web Vitals" },
  { name: "Cross-Channel-Verzahnung", detail: "mit SEA, SEO, Tracking" },
];

const stack = [
  { name: "Google Analytics (GA4)", small: "Verhalten, Funnel-Daten" },
  { name: "Google Tag Manager", small: "Event-Tracking, Pflege" },
  { name: "Hotjar / Microsoft Clarity", small: "Heatmaps, Session-Recordings" },
  { name: "PageSpeed Insights / Lighthouse", small: "Technische Performance" },
];

function CWOPage() {
  return (
    <>
      <Topbar />
      <main id="main">
        <Hero
          variant="short"
          eyebrow="Conversion- & Web-Optimierung"
          headline={<>Conversion ist <em>kein Pop-up-Trick.</em> Sie ist Architektur.</>}
          lead="Wir verbinden Web-Entwicklung und Conversion-Optimierung als ein zusammenhängendes Gewerk — denn ein Conversion-Programm auf einer technisch maroden Website ist verschenkte Arbeit."
          breadcrumbs={[{ label: "Start", to: "/" }, { label: "Leistungen", to: "/leistungen" }, { label: "Conversion- & Web-Optimierung" }]}
        />

        <Section number="02" kicker="Standpunkt" headline={<>Jeder Prozentpunkt mehr Conversion <em>wirkt direkt.</em></>}>
          <RevealOnScroll>
            <div className="prose max-w-2xl text-[17px] leading-relaxed text-ink-2">
              <p className="mb-5">Viele Unternehmen investieren erhebliche Budgets in Performance Marketing oder SEO – und schöpfen dennoch nur einen Teil des Potenzials aus. Der Grund ist selten mangelnder Traffic. Der Grund ist fast immer ein System, das Nutzer nicht konsequent zur gewünschten Handlung führt.</p>
              <p className="mb-5">Conversion-Optimierung bedeutet für uns nicht kosmetische Anpassungen oder isolierte A/B-Tests. Sie bedeutet, systematisch zu analysieren, an welcher konkreten Stelle Nutzer abspringen, welche Informationen dort fehlen – und wie viele Abschlüsse dadurch aktuell verloren gehen.</p>
              <p>Jeder Prozentpunkt mehr Conversion wirkt direkt auf Umsatz, CPL, CPA und ROAS. Conversion-Optimierung ist für uns kein isoliertes Disziplin, sondern ein integraler Bestandteil eines wirtschaftlich gedachten Online-Systems — vom ersten Klick bis zum Abschluss.</p>
            </div>
          </RevealOnScroll>
          <Callout>Conversion-Optimierung bedeutet nicht, alles zu verändern. Sondern gezielt dort anzusetzen, wo messbar Geld verloren geht.</Callout>
        </Section>

        <Section variant="tinted" number="03" kicker="Vorgehen" headline={<>Vier Phasen vom <em>Funnel zum Hebel.</em></>}>
          <ProcessFlow steps={steps} />
          <Callout>Traffic ohne Conversion ist verbranntes Potenzial. Conversion ist der Multiplikator für alle Marketingkanäle.</Callout>
        </Section>

        <Section number="04" kicker="Capabilities & Stack" headline={<>Womit wir Conversion <em>steuerbar</em> machen.</>}>
          <Capabilities items={capabilities} />
          <div className="mt-12">
            <p className="eyebrow mb-5">Stack</p>
            <StackGrid items={stack} />
          </div>
        </Section>

        <LogoReferences locale="de" number="05" />

        <RelatedServices
          locale="de"
          fromId="conversion-web-optimierung"
          number="06"
          items={relatedBySource["conversion-web-optimierung"]}
        />

        <Section number="07" kicker="Engagement">
          <CTABlock
            headline={<>Wie viel Potenzial liegt aktuell <em>ungenutzt</em> auf Ihrer Website?</>}
            body="Sie möchten verstehen, an welchen Stellen Nutzer abspringen – und wie sich das systematisch ändern lässt?"
            primary={{ label: "Kontakt aufnehmen", to: "/kontakt" }}
            secondary={{ label: "Alle Leistungen", to: "/leistungen" }}
          />
        </Section>
        <GlossaryTerms locale="de" ids={["conversion-rate-optimization", "conversion-rate", "ab-test", "funnel-analysis", "conversion-funnel"]} />
      </main>
      <Footer />
    </>
  );
}
